// Import 3rd-party dependencies
import { Component, EventEmitter, Output, Input, ViewChild, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

// Import project-specific dependencies
import { AppToolsService, IResourceLink } from '@app/services/app-tools/app-tools.service';
import { UserProfileService } from '@app/services/user-profile/user-profile.service';
import { CUSTOM_TOOLS_RESOURCE_CONTROL_COMPONENT_CONSTS } from '@app/config/constants';

@Component({
  selector: 'app-custom-tools-resource-control',
  templateUrl: './custom-tools-resource-control.component.html',
  styleUrls: ['./custom-tools-resource-control.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomToolsResourceControlComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomToolsResourceControlComponent),
      multi: true
    }
  ]
})
export class CustomToolsResourceControlComponent implements ControlValueAccessor, Validator {
  private searchAction$ = new BehaviorSubject<string>('');
  @Output() resourceLinkChange = new EventEmitter<IResourceLink[]>();
  @Input() listTools = [];
  @ViewChild(MatAutocompleteTrigger) autoComplete: MatAutocompleteTrigger;
  searchValue = new FormControl('');
  arrIResourceLink = [];
  oldOption: IResourceLink;

  selection: IResourceLink;
  touched = false;
  consts = CUSTOM_TOOLS_RESOURCE_CONTROL_COMPONENT_CONSTS;

  resourceList$ = combineLatest([
    this.searchAction$,
    this.appToolsService.allResourceLinks$,
    this.userProfileService.resourceLinks$
  ]).pipe(
    map(([search, links, fav]) => {
      search = search.trim().toLocaleLowerCase();
      if (search.length === 0) {
        return links.filter(
          (link) =>
            !fav.links.find((fv) => fv.name === link.name) &&
            !(this.listTools.length > 0 && this.listTools.indexOf(link.name) > -1)
        );
      } else {
        const links$ = links.filter(
          (link) => link.name.toLocaleLowerCase().includes(search) || link.keywords.toLocaleLowerCase().includes(search)
        );
        return links$.filter(
          (link) =>
            !fav.links.find((fv) => fv.name === link.name) &&
            !(this.listTools.length > 0 && this.listTools.indexOf(link.name) > -1)
        );
      }
    })
  );

  constructor(private appToolsService: AppToolsService, private userProfileService: UserProfileService) {}

  validate({ value }: AbstractControl): ValidationErrors | null {
    if (!(<IResourceLink>value).name) {
      return {
        required: true
      };
    }
  }

  onChange = (resourceLink?: IResourceLink): void => {}; // eslint-disable-line @typescript-eslint/no-unused-vars
  onTouched = (): void => {};

  writeValue(resourceLink: IResourceLink): void {
    this.selection = resourceLink;
  }

  registerOnChange(onChange: (resource?: IResourceLink) => unknown): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => unknown): void {
    this.onTouched = onTouched;
  }

  togglePanel(): void {
    this.autoComplete.panelOpen ? this.autoComplete.closePanel() : this.autoComplete.openPanel();
  }

  searchLinks(): void {
    this.searchAction$.next(this.searchValue.value);
  }

  getOptionText(option: IResourceLink): string {
    return option != null ? option.name : '';
  }

  updateMySelection(option: IResourceLink): void {
    this.arrIResourceLink = [];
    if (this.oldOption != undefined) {
      this.arrIResourceLink.push(this.oldOption);
    }
    this.oldOption = option;
    this.arrIResourceLink.push(option);
    this.resourceLinkChange.emit(this.arrIResourceLink);

    this.markAsTouched();
    this.onChange(option);
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }
}
